import { logger } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../stores/projectStore';
import { useMachineStore } from '../stores/machineStore';
import { useUIStore } from '../stores/uiStore';
import { Folder, CheckCircle, Plus, Loader2, Settings, ChevronDown } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { isWeb } from '../lib/platform';
import {
  sortProjects,
  partitionByActive,
  groupByTeam,
  isProjectActive,
  openTerminalPaths,
  PROJECT_DND_MIME,
  UNGROUPED_ID,
} from '../lib/projectSort';
import { getProjectGroupId, setProjectGroupId } from '../lib/projectSettings';
import { useTerminalStore } from '../stores/terminalStore';
import { useGroupStore } from '../stores/groupStore';
import type { Project } from '../types/tauri';
import UserIndicator from '../components/UserIndicator';
import SortControl from '../components/SortControl';
import TeamContextMenu from '../components/workspace/TeamContextMenu';

export default function Home() {
  const navigate = useNavigate();
  const projects = useProjectStore((state) => state.projects);
  const loadProjects = useProjectStore((state) => state.loadProjects);
  const updateProject = useProjectStore((state) => state.updateProject);
  const setCurrentProject = useProjectStore((state) => state.setCurrentProject);
  const sortMode = useUIStore((state) => state.projectSortMode);
  const sortDir = useUIStore((state) => state.projectSortDir);
  const inactiveExpanded = useUIStore((state) => state.inactiveGroupExpanded);
  const toggleInactiveGroup = useUIStore((state) => state.toggleInactiveGroup);
  const collapsedTeams = useUIStore((state) => state.collapsedTeams);
  const toggleTeamCollapsed = useUIStore((state) => state.toggleTeamCollapsed);
  const groups = useGroupStore((state) => state.groups);
  const loadGroups = useGroupStore((state) => state.loadGroups);
  const [showAll, setShowAll] = useState(false);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ projectId: string; x: number; y: number } | null>(null);
  const webMode = isWeb();

  // Re-render when terminals open/close so the Active/Inactive grouping stays live.
  useTerminalStore((state) => state.sessions);
  const autoNavigatedRef = useRef(false);

  // Auto-navigate to last active project once connection is ready
  const currentProject = useProjectStore((state) => state.currentProject);
  const connectionState = useMachineStore((state) => state.connectionState);
  const webrtcReady = useMachineStore((state) => state.webrtcReady);
  const webrtcConnecting = useMachineStore((state) => state.webrtcConnecting);
  useEffect(() => {
    if (autoNavigatedRef.current) return;
    if (!currentProject?.id) return;

    // In web mode, wait for full WebRTC pipeline (not just signaling auth)
    const isReady = webMode ? webrtcReady : true;
    if (!isReady) return;

    autoNavigatedRef.current = true;
    navigate(`/workspace/${currentProject.id}`, { replace: true });
  }, [currentProject?.id, connectionState, webrtcReady, webMode, navigate]);

  // Auto-select first project when no currentProject is persisted
  useEffect(() => {
    if (autoNavigatedRef.current) return;
    if (currentProject?.id) return;

    const activeProjects = projects.filter((p) => p.status === 'active');
    if (activeProjects.length === 0) return;

    const sorted = sortProjects(activeProjects, sortMode);
    const firstProject = sorted[0];
    if (firstProject) {
      const isReady = webMode ? webrtcReady : true;
      if (!isReady) return;

      // Persist selection so the existing auto-navigate effect works on next visit
      setCurrentProject(firstProject.id);
      autoNavigatedRef.current = true;
      navigate(`/workspace/${firstProject.id}`, { replace: true });
    }
  }, [projects, currentProject?.id, sortMode, webMode, webrtcReady, navigate, setCurrentProject]);

  useEffect(() => {
    loadProjects();
    void loadGroups();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadProjects();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadProjects, loadGroups]);

  const activeProjects = projects.filter((p) => p.status === 'active');
  const displayProjects = showAll ? projects : activeProjects;
  const sortedProjects = sortProjects(displayProjects, sortMode, sortDir);
  // Group by terminal activity (has an open terminal), independent of the
  // status badge / "Show All" archived filter above.
  const { active: activeGroup, inactive: inactiveGroup } = partitionByActive(sortedProjects);

  const hasTeams = groups.length > 0;
  const openPaths = openTerminalPaths();

  // Reassign a project's team (persisted in its settings blob). No-op if same.
  const assignToTeam = async (projectId: string, teamId: string | null) => {
    const p = projects.find((x) => x.id === projectId);
    if (!p || getProjectGroupId(p.settings) === teamId) return;
    try {
      await updateProject(projectId, { settings: setProjectGroupId(p.settings, teamId) });
    } catch { /* store logs it */ }
  };
  const onDropToTeam = (e: React.DragEvent, teamId: string | null) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = e.dataTransfer.getData(PROJECT_DND_MIME);
    if (id) void assignToTeam(id, teamId);
  };

  const handleActivateProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateProject(projectId, { status: 'active' });
      await loadProjects();
    } catch (error) {
      logger.error('Home', 'Failed to activate project:', error);
    }
  };

  const renderProjectCard = (project: Project) => (
    <div
      key={project.id}
      draggable={hasTeams}
      onDragStart={hasTeams ? (e) => { e.dataTransfer.setData(PROJECT_DND_MIME, project.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
      onContextMenu={hasTeams ? (e) => { e.preventDefault(); setMenu({ projectId: project.id, x: e.clientX, y: e.clientY }); } : undefined}
      onClick={() => navigate(`/workspace/${project.id}`)}
      className="card bg-base-200 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
    >
      <div className="card-body">
        <div className="flex items-start justify-between">
          <h3 className="card-title text-lg">{project.name}</h3>
          {project.status !== 'active' && (
            <button
              onClick={(e) => handleActivateProject(project.id, e)}
              className="btn btn-xs btn-success gap-1"
              title="Activate project"
            >
              <CheckCircle className="w-3 h-3" />
              Activate
            </button>
          )}
        </div>

        {project.prd_content && (
          <p className="text-sm text-base-content/70 line-clamp-2">
            {project.prd_content}
          </p>
        )}

        <div className="flex flex-wrap gap-2 mt-2">
          <div className={`badge badge-sm ${project.status === 'active' ? 'badge-primary' : 'badge-ghost'}`}>
            {project.agent_type}
          </div>
          <div className={`badge badge-sm ${project.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
            {project.status}
          </div>
        </div>

        <div className="text-xs text-base-content/60 mt-2">
          {project.last_activity && project.last_activity !== project.created_at
            ? `Opened ${new Date(project.last_activity).toLocaleDateString()}`
            : `Created ${new Date(project.created_at).toLocaleDateString()}`}
        </div>
      </div>
    </div>
  );

  // The Active grid + collapsible Inactive grid for one set of projects —
  // reused for the flat (no-teams) view and inside each team section.
  const renderActiveInactiveGrids = (active: Project[], inactive: Project[]) => (
    <>
      {active.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-emerald-500/80">
              Active ({active.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {active.map(renderProjectCard)}
          </div>
        </div>
      )}
      {inactive.length > 0 && (
        <div>
          <button
            onClick={toggleInactiveGroup}
            className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity"
            title={inactiveExpanded ? 'Collapse inactive' : 'Expand inactive'}
          >
            <ChevronDown className={`w-4 h-4 text-base-content/40 transition-transform ${inactiveExpanded ? '' : '-rotate-90'}`} />
            <span className="w-2 h-2 rounded-full bg-base-content/30" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/40">
              Inactive ({inactive.length})
            </h3>
          </button>
          {inactiveExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inactive.map(renderProjectCard)}
            </div>
          )}
        </div>
      )}
    </>
  );

  // Show connecting screen only during active connection attempt
  // Once the attempt finishes (success or failure), show the home page
  // If WebRTC connects, the auto-navigate effect above will redirect to workspace
  const isWaitingForConnection = webMode && currentProject?.id && !autoNavigatedRef.current
    && (connectionState !== 'authenticated' || webrtcConnecting);
  if (isWaitingForConnection) {
    return (
      <div className="h-full bg-base-100 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
          <div>
            <p className="text-lg font-medium">Connecting...</p>
            <p className="text-sm text-base-content/60 mt-1">
              Restoring {currentProject.name}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-base-100 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="navbar bg-base-200 border-b border-base-300 pl-14 md:pl-4">
        <div className="flex-1">
          <span className="text-lg font-semibold">AtelierCode</span>
        </div>
        <div className="flex-none gap-2 flex items-center">
          <button
            onClick={() => useUIStore.getState().openSettings()}
            className="btn btn-ghost btn-sm btn-square"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </button>
          {webMode && <UserIndicator />}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        {sortedProjects.length === 0 && displayProjects.length === 0 ? (
          // Empty State
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-24 h-24 rounded-full bg-base-200 flex items-center justify-center mb-6">
              <Folder className="w-12 h-12 text-base-content/40" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">No Projects Yet</h2>
            <p className="text-base-content/70 mb-8 text-center max-w-md">
              Create your first AI-assisted development project to get started
            </p>
            <button
              onClick={() => navigate('/wizard')}
              className="btn btn-primary btn-lg gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Your First Project
            </button>
          </div>
        ) : (
          // Projects List
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  Projects
                </h2>
                <div className="flex items-center gap-3">
                  <SortControl />
                  {projects.length > activeProjects.length && (
                    <label className="label cursor-pointer gap-2">
                      <span className="label-text text-sm">Show All</span>
                      <input
                        type="checkbox"
                        className="toggle toggle-sm"
                        checked={showAll}
                        onChange={(e) => setShowAll(e.target.checked)}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Grouped by status: Active (open terminal) above a collapsible
                  Inactive section. When Teams are defined, this whole block is
                  repeated per team (plus Ungrouped), each team header a
                  drop target for dragging a project card into it. */}
              {!hasTeams && (
                <div className="space-y-5">
                  {renderActiveInactiveGrids(activeGroup, inactiveGroup)}
                </div>
              )}
              {hasTeams && (
                <div className="space-y-6">
                  {groupByTeam(sortedProjects, groups, (p) => isProjectActive(p, openPaths)).map((bucket) => {
                    const key = bucket.group?.id ?? UNGROUPED_ID;
                    const name = bucket.group?.name ?? 'Ungrouped';
                    const color = bucket.group?.color ?? null;
                    const count = bucket.active.length + bucket.inactive.length;
                    const collapsed = collapsedTeams.includes(key);
                    const dropping = dragOverKey === key;
                    return (
                      <div
                        key={key}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverKey !== key) setDragOverKey(key); }}
                        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey((k) => (k === key ? null : k)); }}
                        onDrop={(e) => onDropToTeam(e, bucket.group?.id ?? null)}
                        className={`rounded-xl transition-colors ${dropping ? 'ring-2 ring-primary/50 bg-primary/5 p-2 -m-2' : ''}`}
                      >
                        <button
                          onClick={() => toggleTeamCollapsed(key)}
                          className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity"
                          title={collapsed ? `Expand ${name}` : `Collapse ${name}`}
                        >
                          <ChevronDown className={`w-4 h-4 text-base-content/50 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                          <span className="w-2.5 h-2.5 rounded-full border border-base-content/20" style={{ backgroundColor: color ?? 'transparent' }} />
                          <h3 className="text-sm font-bold uppercase tracking-wider text-base-content/70">{name}</h3>
                          <span className="text-xs text-base-content/40">({count})</span>
                        </button>
                        {!collapsed && (
                          <div className="space-y-5 pl-1">
                            {count === 0
                              ? <div className="text-xs text-base-content/40 italic px-1">Drag a project here</div>
                              : renderActiveInactiveGrids(bucket.active, bucket.inactive)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right-click a card: move that project to a team. */}
      {menu && (
        <TeamContextMenu
          x={menu.x}
          y={menu.y}
          currentTeamId={getProjectGroupId(projects.find((p) => p.id === menu.projectId)?.settings ?? null)}
          groups={groups}
          onPick={(teamId) => { void assignToTeam(menu.projectId, teamId); setMenu(null); }}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
